import { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { Search, Heart, User, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { useWishlistStore } from '@/store/wishlist'

const IconInstagram = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none"/>
  </svg>
)
const IconPinterest = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
)
const IconX = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.729-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)
const IconFacebook = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
)

interface NavItem { label: string; to: string; className?: string }

const NAV_LEFT: NavItem[]  = [
  { label: 'New In',  to: '/new-in' },
  { label: 'Women',   to: '/women' },
  { label: 'Men',     to: '/men' },
]

const NAV_RIGHT: NavItem[] = [
  { label: 'Beauty',  to: '/beauty' },
  { label: 'Brands',  to: '/brands' },
  { label: 'Sale',    to: '/sale', className: 'text-[#d4952a]' },
]

const ALL_NAV: NavItem[] = [...NAV_LEFT, ...NAV_RIGHT]

export function Header() {
  const [searchOpen, setSearchOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { session } = useAuthStore()
  const { productIds } = useWishlistStore()

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`)
      setSearchOpen(false)
      setQuery('')
    }
  }

  const navLinkClass = (isActive: boolean, extra?: string) =>
    cn(
      'text-[10px] font-body font-medium tracking-[0.2em] uppercase transition-colors whitespace-nowrap',
      isActive ? 'text-[#d4952a]' : 'text-[#9ca3af] hover:text-[#f0ebe0]',
      extra
    )

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-[#06090c]/95 backdrop-blur-md border-b border-white/5">
        {/* Announcement bar */}
        <div className="bg-[#1a4d33] text-[#f0ebe0] text-[10px] py-1.5 font-body tracking-widest">
          <div className="w-full px-6 sm:px-10 md:px-16 lg:px-24 xl:px-32 flex items-center justify-between">
            {/* Left: social icons */}
            <div className="flex items-center gap-3">
              <a href="https://instagram.com/maravelle" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                className="opacity-60 hover:opacity-100 transition-opacity">
                <IconInstagram />
              </a>
              <a href="https://pinterest.com/maravelle" target="_blank" rel="noopener noreferrer" aria-label="Pinterest"
                className="opacity-60 hover:opacity-100 transition-opacity">
                <IconPinterest />
              </a>
              <a href="https://x.com/maravelle" target="_blank" rel="noopener noreferrer" aria-label="X"
                className="opacity-60 hover:opacity-100 transition-opacity">
                <IconX />
              </a>
              <a href="https://facebook.com/maravelle" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                className="opacity-60 hover:opacity-100 transition-opacity">
                <IconFacebook />
              </a>
            </div>
            {/* Centre: main message */}
            <span className="text-center">Free returns on all UK orders · Spring / Summer 2025</span>
            {/* Right: secondary message */}
            <span className="opacity-70 hidden sm:block">Curated from Britain's finest brands</span>
          </div>
        </div>

        {/* Main nav bar — three equal columns: left nav | brand | right nav + actions */}
        <div className="w-full px-6 sm:px-10 md:px-16 lg:px-24 xl:px-32 h-16 grid grid-cols-[1fr_auto_1fr] items-center gap-2">

          {/* Left nav links */}
          <nav className="hidden md:flex items-center gap-5 justify-end pr-8">
            {NAV_LEFT.map(({ label, to }) => (
              <NavLink key={to} to={to} className={({ isActive }) => navLinkClass(isActive)}>
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Brand name — always centred */}
          <Link
            to="/"
            className="font-display text-2xl font-light tracking-[0.2em] text-[#f0ebe0] hover:text-[#d4952a] transition-colors text-center"
          >
            MARAVELLE
          </Link>

          {/* Right nav links + actions */}
          <div className="hidden md:flex items-center gap-5 justify-start pl-8">
            {NAV_RIGHT.map(({ label, to, className }) => (
              <NavLink key={to} to={to} className={({ isActive }) => navLinkClass(isActive, className)}>
                {label}
              </NavLink>
            ))}

            <div className="flex items-center gap-4 ml-auto pl-4 border-l border-white/10">
              <button onClick={() => setSearchOpen(true)} aria-label="Search"
                className="text-[#9ca3af] hover:text-[#d4952a] transition-colors">
                <Search className="h-4 w-4" />
              </button>

              <Link to="/wishlist" aria-label={`Wishlist (${productIds.size})`}
                className="relative text-[#9ca3af] hover:text-[#d4952a] transition-colors">
                <Heart className="h-4 w-4" />
                {productIds.size > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#d4952a] text-[#06090c] text-[9px] font-bold flex items-center justify-center font-body">
                    {productIds.size}
                  </span>
                )}
              </Link>

              <Link to={session ? '/account' : '/auth/sign-in'} aria-label="Account"
                className="text-[#9ca3af] hover:text-[#d4952a] transition-colors">
                <User className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Mobile: search + hamburger on right */}
          <div className="md:hidden flex items-center gap-3 justify-end">
            <button onClick={() => setSearchOpen(true)} aria-label="Search"
              className="text-[#9ca3af] hover:text-[#d4952a] transition-colors">
              <Search className="h-5 w-5" />
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu"
              className="text-[#9ca3af] hover:text-[#d4952a] transition-colors">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile: empty left slot so brand stays centred */}
          <div className="md:hidden" />
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-[#06090c]/95 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={() => setSearchOpen(false)}>
          <form className="w-full max-w-2xl" onClick={e => e.stopPropagation()} onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6b7280]" />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search brands, products, styles…"
                className="w-full h-14 pl-12 pr-4 bg-[#0d1117] border border-[#d4952a]/40 rounded-sm text-[#f0ebe0] font-body text-base placeholder:text-[#6b7280] focus:outline-none focus:border-[#d4952a]"
              />
            </div>
            <button onClick={() => setSearchOpen(false)}
              className="mt-4 text-xs text-[#6b7280] font-body tracking-widest uppercase hover:text-[#d4952a]">
              Press Esc to close
            </button>
          </form>
        </div>
      )}

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[calc(1.625rem+4rem)] z-40 bg-[#06090c] border-t border-white/5 px-6 py-8 flex flex-col gap-1">
          {ALL_NAV.map(({ label, to, className }) => (
            <Link key={to} to={to} onClick={() => setMobileOpen(false)}
              className={cn(
                'text-sm font-body font-medium tracking-[0.2em] uppercase text-[#9ca3af] hover:text-[#d4952a] transition-colors py-3 border-b border-white/5',
                className
              )}>
              {label}
            </Link>
          ))}
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-white/10">
            <Link to="/wishlist" className="flex items-center gap-2 text-xs text-[#9ca3af] font-body tracking-widest uppercase hover:text-[#d4952a]">
              <Heart className="h-4 w-4" /> Wishlist {productIds.size > 0 && `(${productIds.size})`}
            </Link>
            <Link to={session ? '/account' : '/auth/sign-in'} className="flex items-center gap-2 text-xs text-[#9ca3af] font-body tracking-widest uppercase hover:text-[#d4952a]">
              <User className="h-4 w-4" /> Account
            </Link>
          </div>
        </div>
      )}
    </>
  )
}
